import qs from 'query-string';
import { useTranslation } from 'react-i18next';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {
  LinkMetaType,
  McrContactType,
  MutationType,
  SurveyResponseType,
  SurveysResponseType,
  SurveysResponsesResponseType,
} from 'shared/types';
import request from 'utils/request';
import { BulkActionsResponse } from './types';
import { RESPONSE_PER_PAGE } from './constants';
import i18n from '../../../i18n';

type rQueryType = {
  id: string;
  ratingQuestion: any;
  currentSurvey: any;
  responseFilterCriteria: any;
};

export const useGetAllSurveysInfinite = () =>
  useInfiniteQuery(
    'surveys-infinite',
    async ({ pageParam = '' }) => {
      const url = pageParam
        ? `/surveyserv/surveys/${new URL(pageParam).search}&is_template=false`
        : '/surveyserv/surveys/?is_template=false';
      return await request(url);
    },
    {
      getPreviousPageParam: (firstPage: any) =>
        firstPage._links?.prev?.href ?? false,
      getNextPageParam: (lastPage: any) => lastPage._links?.next?.href ?? false,
    },
  );

export const useGetSurveys = (enabled: boolean) =>
  useQuery(
    'default-surveys',
    async () => {
      const data: SurveysResponseType = await request(
        '/surveyserv/surveys/?is_template=false&state=ACTIVE&state=PAUSE',
      );
      return data.surveys || [];
    },
    {
      enabled,
    },
  );

export const useGetResponses = ({
  surveyId,
  appliedFilters,
  callback,
  pageToken,
}: MutationType & {
  surveyId: string;
  appliedFilters: any;
  pageToken: string;
}) =>
  useQuery<SurveysResponsesResponseType>(
    `survey-response-${surveyId}`,
    async () => {
      let params = '';
      const next_token = pageToken;
      const { filterParams: appliedFilterParams } = appliedFilters;
      const filterParams = `${
        appliedFilterParams?.length > 0 ? appliedFilterParams : ''
      }`;

      params = filterParams
        ? `${filterParams}${next_token ? `&next_token=${next_token}` : ''}`
        : next_token
        ? `next_token=${next_token}`
        : '';
      params = Array.from(new Set(params.split('&'))).join('&') || params;
      const perPageParams = params.length
        ? `page_size=${RESPONSE_PER_PAGE}&${params}`
        : `page_size=${RESPONSE_PER_PAGE}`;

      const url = `/surveys/${surveyId}/responses?${qs.stringify({
        status: ['COMPLETE', 'PARTIAL_COMPLETE'],
        ...qs.parse(perPageParams),
      })}`;
      return await request(url);
    },
    {
      enabled: typeof surveyId === 'string',
      onSuccess: (response) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
      refetchOnWindowFocus: true,
      staleTime: 30000,
    },
  );

export const useGetResponseCount = (data: rQueryType) =>
  useQuery(
    [`response-count-${data.id}`],
    async () => {
      let params = '';

      const { filterParams: appliedFilterParams } = data.responseFilterCriteria;
      const filterParams = `${
        appliedFilterParams?.length > 0 ? appliedFilterParams : ''
      }`;

      params = filterParams ? `${filterParams}` : '';
      params = Array.from(new Set(params.split('&'))).join('&') || params; // remove duplicates
      return await request(
        `/surveys/${data.id}/responses/count?${qs.stringify({
          status: ['COMPLETE', 'PARTIAL_COMPLETE'],
          ...qs.parse(params),
        })}`,
      );
    },
    {
      enabled: !!(data.ratingQuestion && data.currentSurvey && data.id),
      refetchOnWindowFocus: true,
      staleTime: 30000,
    },
  );

export const useGetContact = (
  contactId: string,
  { callback, refetchOnWindowFocus = false }: MutationType = {},
) => {
  return useQuery(
    ['contact', contactId],
    async () => {
      const data: McrContactType = await request(`/contacts/${contactId}`);
      return data;
    },
    {
      onSuccess: (response: McrContactType) => {
        if (callback) callback(true, response);
      },
      enabled: !!contactId,
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      refetchOnWindowFocus,
    },
  );
};

type DeleteResponseMutationType = {
  surveyId?: string;
  ratingType?: string;
  responseId?: string;
};

export const useDeleteResponseMutation = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ surveyId, responseId }: DeleteResponseMutationType) => {
      await request(
        `/surveys/${surveyId}/responses/${responseId}`,
        {},
        'DELETE',
      );
    },
    {
      onSuccess: (response: any, data: DeleteResponseMutationType) => {
        queryClient.invalidateQueries(`survey-response-${data.surveyId}`);
        queryClient.invalidateQueries(`response-count-${data.surveyId}`);
        // queryClient.invalidateQueries(`response-count-${data.surveyId}-all`);
        if (setToast)
          setToast({
            delay: 3000,
            type: 'success',
            text: `${i18n.t('forms.toast.success.response_deleted')}`,
          });
        if (callback) callback(true, data);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: `${i18n.t('forms.toast.error.response_deletion')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetResponsesDetails = (data: {
  surveyId: string;
  responseId: string;
}) =>
  useQuery<SurveyResponseType>(
    `response-${data.responseId}`,
    async () => {
      return await request(
        `/surveys/${data.surveyId}/responses/${data.responseId}`,
      );
    },
    {
      enabled: !!data.surveyId && !!data.responseId,
      cacheTime: 0,
    },
  );

type GetResponseNavigateType = {
  responseId: string;
  surveyId: string;
  filterParams: string;
};

export const useSaveResponseTranslation = ({ callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      survey_id,
      response_id,
      targetLanguage,
      sourceLanguage,
    }: {
      survey_id: string;
      response_id: string;
      targetLanguage: any;
      sourceLanguage: any;
    }) => {
      return await request(
        `/surveys/${survey_id}/responses/${response_id}/translate?targetLanguage=${targetLanguage}&sourceLanguage=${sourceLanguage}`,
        {},
        'GET',
      );
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('response-translation');
        if (callback) callback(true, response);
      },
    },
  );
};

export const useGetResponseNavigate = (data: GetResponseNavigateType) =>
  useQuery<{
    surveyResponses: SurveyResponseType[];
    _links: LinkMetaType;
  }>(
    `response-${data.responseId}-navigate`,
    async () => {
      const url =
        data.filterParams === ''
          ? `/surveys/${data.surveyId}/responses/${data.responseId}/navigate/?page_size=${RESPONSE_PER_PAGE}`
          : `/surveys/${data.surveyId}/responses/${data.responseId}/navigate/?page_size=${RESPONSE_PER_PAGE}&${data.filterParams}`;
      return await request(url);
    },
    {
      enabled: !!data.surveyId && !!data.responseId,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );

export const useUpdateResponseMutation = (setToast: any) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      surveyId,
      responseId,
      updatedData,
    }: {
      surveyId: string;
      responseId: string;
      updatedData: any;
      edited: any;
      commentQuestion: any;
    }) => {
      await request(
        `/surveys/${surveyId}/responses/${responseId}`,
        updatedData,
        'PUT',
      );
    },
    {
      onSuccess: (response: any, { updatedData }: any) => {
        queryClient.invalidateQueries(`response-${updatedData?.id}-navigate`);
        setToast({
          delay: 3000,
          type: 'success',
          text: `${i18n.t('forms.toast.success.response_updated')}`,
        });
      },
      onError: () => {
        setToast({
          delay: 3000,
          type: 'error',
          text: `${i18n.t('forms.toast.error.response_updated')}`,
        });
      },
    },
  );
};

export const useUpdateNonActionable = (setToast: any) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      surveyId,
      responseId,
      nonactionable,
    }: {
      surveyId?: string;
      responseId?: string;
      response_display_id?: string;
      nonactionable?: boolean;
    }) => {
      await request(
        `/surveys/${surveyId}/responses/${responseId}/nonactionable`,
        {},
        nonactionable ? 'PUT' : 'DELETE',
      );
    },
    {
      onSuccess: (
        response: any,
        { surveyId, responseId, nonactionable, response_display_id }: any,
      ) => {
        queryClient.invalidateQueries(
          `notes-${surveyId}-${response_display_id}`,
        );
        queryClient.setQueryData(
          `response-${response_display_id}-navigate`,
          (qData: any) => {
            if (qData?.surveyResponses?.[0])
              qData.surveyResponses[0].is_non_actionable = nonactionable;
            return qData;
          },
        );
        setToast({
          delay: 3000,
          type: 'success',
          text: nonactionable
            ? `${i18n.t(
                'forms.toast.success.response_marked_as_non_actionable',
              )}`
            : `${i18n.t('forms.toast.success.response_marked_as_actionable')}`,
        });
      },
      onError: () => {
        setToast({
          delay: 3000,
          type: 'error',
          text: `${i18n.t('forms.toast.error.response_updated')}`,
        });
      },
    },
  );
};

export const useResponseFavouriteMutation = (setToast: any) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      surveyId,
      responseId,
      fav,
    }: {
      surveyId?: string;
      responseId?: string;
      response_display_id?: string;
      fav?: boolean;
    }) => {
      await request(
        `/surveys/${surveyId}/responses/${responseId}/favorite`,
        {},
        fav ? 'DELETE' : 'PUT',
      );
    },
    {
      onSuccess: (response: any, data: any) => {
        queryClient.invalidateQueries(
          `notes-${data.surveyId}-${data.response_display_id}`,
        );
        queryClient.setQueryData(
          `response-${data.response_display_id}-navigate`,
          (qData: any) => {
            if (qData?.surveyResponses?.[0])
              qData.surveyResponses[0].is_favourite = !data.fav;
            return qData;
          },
        );
        setToast({
          delay: 3000,
          type: 'success',
          text: !data.fav
            ? `${i18n.t('forms.toast.success.response_favorited')}`
            : `${i18n.t('forms.toast.success.response_updated')}`,
        });
      },
      onError: () => {
        setToast({
          delay: 3000,
          type: 'error',
          text: `${i18n.t('forms.toast.error.response_updated')}`,
        });
      },
    },
  );
};

export const useResponseInvalidMutation = (setToast: any) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      surveyId,
      responseId,
      invalid,
      invalidMessage,
    }: {
      surveyId?: string;
      responseId?: string;
      invalid?: boolean;
      invalidMessage?: string;
      response_display_id?: string;
    }) => {
      return await request(
        `/surveys/${surveyId}/responses/${responseId}/invalid`,
        invalid ? { invalidReason: invalidMessage } : {},
        invalid ? 'PUT' : 'DELETE',
      );
    },
    {
      onSuccess: (
        response: any,
        {
          responseId,
          surveyId,
          invalid,
          invalidMessage,
          response_display_id,
        }: any,
      ) => {
        queryClient.setQueryData(
          `response-${response_display_id}-navigate`,
          (qData: any) => {
            if (qData?.surveyResponses?.[0]) {
              qData.surveyResponses[0].is_invalid = invalid;
              qData.surveyResponses[0].invalid_reason = invalid
                ? invalidMessage
                : '';
            }
            return qData;
          },
        );
        queryClient.invalidateQueries(
          `notes-${surveyId}-${response_display_id}`,
        );
        setToast({
          delay: 3000,
          type: 'success',
          text: invalid
            ? `${i18n.t('forms.toast.success.response_marked_as_invalid')}`
            : `${i18n.t('forms.toast.success.response_updated')}`,
        });
      },
      onError: () => {
        setToast({
          delay: 3000,
          type: 'error',
          text: `${i18n.t('forms.toast.error.response_invalidation')}`,
        });
      },
    },
  );
};

export const useGetWorkflows = () =>
  useQuery(
    'all-workflows',
    async () => {
      const data = await request('/workflow?showDeleted=true&page_size=100');
      return data?.workflows;
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 10000,
    },
  );

type NoteMutationType = {
  surveyId: string;
  responseId: string;
};

export const useGetAllNotes = ({ surveyId, responseId }: NoteMutationType) =>
  useInfiniteQuery(
    `notes-${surveyId}-${responseId}`,
    async ({ pageParam = '' }) => {
      const query = new URLSearchParams(pageParam);
      const next_token = query.get('next_token');
      const url = pageParam
        ? `/surveys/${surveyId}/responses/${responseId}/note?next_token=${next_token}&sort_by=created_at;asc`
        : `/surveys/${surveyId}/responses/${responseId}/note?sort_by=created_at;asc`;
      return await request(url);
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 0,
      cacheTime: 0,
      getPreviousPageParam: (firstPage: any) =>
        firstPage._links?.prev?.href ?? false,
      getNextPageParam: (lastPage: any) => lastPage._links?.next?.href ?? false,
    },
  );

export const useTriggerWorkflow = (setToast: any) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      responseId,
      workflowId,
      surveyId,
    }: {
      responseId: string;
      workflowId: string;
      surveyId: string;
    }) => {
      await request(
        `/trigger/workflow/${workflowId}/responses/${responseId}/surveys/${surveyId}`,
        {},
        'POST',
      );
    },
    {
      onSuccess: (response: any, data) => {
        queryClient.invalidateQueries(`response-${data.responseId}-navigate`);
        queryClient.invalidateQueries(
          `notes-${data.surveyId}-${data.responseId}`,
        );
        queryClient.invalidateQueries('tag-lists');
        setToast({
          delay: 3000,
          type: 'success',
          text: `${i18n.t('forms.toast.success.workflow_trigger')}`,
        });
      },
      onError: (error: any) => {
        setToast({
          delay: 3000,
          type: 'error',
          text:
            error.response.status === 429
              ? `${i18n.t('forms.toast.error.workflow_trigger_limit')}`
              : `${i18n.t('forms.toast.error.workflow_trigger')}`,
        });
      },
    },
  );
};

export const useResponseNoteMutation = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      responseId,
      surveyId,
      payload,
      noteId,
    }: {
      responseId: string;
      surveyId: string;
      payload: any;
      noteId?: string;
    }) => {
      const url = noteId
        ? `/surveys/${surveyId}/responses/${responseId}/note/${noteId}`
        : `/surveys/${surveyId}/responses/${responseId}/note`;
      return await request(url, payload, noteId ? 'PUT' : 'POST');
    },
    {
      onSuccess: (response: any, data) => {
        queryClient.invalidateQueries(
          `notes-${data.surveyId}-${data.responseId}`,
        );
        if (setToast)
          setToast({
            delay: 3000,
            type: 'success',
            text: data?.noteId
              ? `${i18n.t('forms.toast.success.note_updated')}`
              : `${i18n.t('forms.toast.success.note_added')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error: any, data) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: data?.noteId
              ? `${i18n.t('forms.toast.error.note_update')}`
              : `${i18n.t('forms.toast.error.add_note')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useDeleteNoteMutation = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      responseId,
      surveyId,
      deleteNoteId,
    }: {
      responseId: string;
      surveyId: string;
      deleteNoteId: string;
    }) => {
      return await request(
        `/surveys/${surveyId}/responses/${responseId}/note/${deleteNoteId}`,
        {},
        'DELETE',
      );
    },
    {
      onSuccess: (response: any, data) => {
        queryClient.invalidateQueries(
          `notes-${data.surveyId}-${data.responseId}`,
        );
        if (setToast)
          setToast({
            delay: 3000,
            type: 'success',
            text: `${i18n.t('forms.toast.success.response_note_deleted')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: `${i18n.t('forms.toast.error.response_note_delete')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetTags = ({
  callback,
  searchParam,
}: MutationType & {
  searchParam: string;
}) =>
  useQuery(
    'tags',
    async () => {
      const newURL = qs.stringifyUrl({
        url: '/account-tag/search',
        query: {
          term: searchParam,
        },
      });
      const data = await request(newURL);
      return data;
    },
    {
      onSuccess: (response: any) => {
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (callback) callback(false, error);
      },
      enabled: searchParam?.length > 0,
      retry: 0,
    },
  );

export const useGetTagInfo = (
  tagIds: string[],
  responseId: string,
  mutations?: MutationType,
) =>
  useQuery(
    `taginfo-${responseId}`,
    async () => {
      if (tagIds.length === 0) return;
      const data = await request(`/account-tag/bulk/?ids=${tagIds}`);
      return data.accountTags;
    },
    {
      enabled: tagIds.length > 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      onSuccess: (response: any) => {
        if (mutations?.callback) mutations.callback(true, response);
      },
      onError: (error: any) => {
        if (mutations?.callback) mutations.callback(false, error);
      },
    },
  );

export const useAddTagMutation = ({ callback }: MutationType) => {
  return useMutation(
    async ({ payload }: { payload: any; responseId: string }) => {
      const data = await request('/account-tag', payload, 'POST');
      return data;
    },
    {
      onSuccess: (response: any) => {
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const useAttachTagWithResponseMutation = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      surveyId,
      responseId,
      tagId,
    }: {
      surveyId: string;
      responseId: string;
      tagId: string;
    }) => {
      return await request(
        `/surveys/${surveyId}/responses/${responseId}/tags/${tagId}`,
        {},
        'PUT',
      );
    },
    {
      onSuccess: (response: any, { tagId }: any) => {
        queryClient.setQueryData(
          `response-${response.id}-navigate`,
          (responseData: any) => {
            if (responseData?.surveyResponses?.[0]) {
              responseData.surveyResponses[0].tags = [
                tagId,
                ...(responseData.surveyResponses[0].tags
                  ? responseData.surveyResponses[0].tags
                  : {}),
              ];
            }
            return responseData;
          },
        );
        if (setToast)
          setToast({
            delay: 3000,
            type: 'success',
            text: `${i18n.t('forms.toast.success.response_updated')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: `${i18n.t('forms.toast.error.response_updated')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useDeleteTagWithResponseMutation = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      surveyId,
      responseId,
      tagId,
    }: {
      surveyId: string;
      responseId: string;
      tagId: string;
    }) => {
      return await request(
        `/surveys/${surveyId}/responses/${responseId}/tags/${tagId}`,
        {},
        'DELETE',
      );
    },
    {
      onSuccess: (response: any, data: any) => {
        queryClient.setQueryData(
          `response-${response.id}-navigate`,
          (responseData: any) => {
            if (responseData?.surveyResponses?.[0]) {
              responseData.surveyResponses[0].tags = (
                responseData.surveyResponses[0].tags || []
              ).filter((item: string) => item !== data.tagId);
              return responseData;
            }
          },
        );
        if (setToast)
          setToast({
            delay: 3000,
            type: 'success',
            text: `${i18n.t('forms.toast.success.response_updated')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: `${i18n.t('forms.toast.error.response_updated')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetAllContactFields = () =>
  useQuery(
    'contact-fields',
    async () => {
      const data = await request('/contact-fields?filterable=true');
      return data?.fields;
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 10000,
    },
  );

export const useExportResponseMutation = ({
  callback,
  setToast,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      surveyId,
      filterParams,
    }: {
      surveyId: string;
      filterParams: string;
    }) => {
      const data = await request(
        `/surveys/${surveyId}/responses/export/${
          filterParams ? `?${filterParams}` : ''
        }`,
        {},
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries('loggedInUser');
        // queryClient.invalidateQueries('account-user');
        if (setToast)
          setToast({
            delay: 10000,
            type: 'success',
            text: `${i18n.t('forms.toast.success.response_initiated')}`,
            showProgress: true,
            fixed: true,
          });
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: `${i18n.t('forms.toast.error.response_export')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useFileDownloadMutation = ({
  callback,
  setToast,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      surveyId,
      filterParams,
    }: {
      surveyId: string;
      filterParams: string;
    }) => {
      const data = await request(
        `/surveys/${surveyId}/responses/file-download${
          filterParams?.length ? `?${filterParams}` : ''
        }`,
        {},
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries('loggedInUser');
        queryClient.invalidateQueries('account-user');
        if (setToast)
          setToast({
            delay: 10000,
            type: 'success',
            text: `${i18n.t('forms.toast.success.download_center_initiated')}`,
            showProgress: true,
            fixed: true,
          });
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: `${i18n.t('forms.toast.error.download_centre')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetSurveyResponseCount = ({
  surveyId,
  enabled = true,
  refetchOnWindowFocus = true,
}: {
  surveyId: string;
} & MutationType) => {
  return useQuery(
    [surveyId, 'response-count'],
    async () => {
      const data = await request(
        `/surveys/${surveyId}/responses/count?status=COMPLETE&status=PARTIAL_COMPLETE`,
      );
      return data;
    },
    {
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      refetchOnWindowFocus,
      enabled: enabled && !!surveyId,
    },
  );
};

export const useBulkActionsResponse = ({ callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ payload }: { payload: BulkActionsResponse }) => {
      return await request('/bulk-actions/response', payload, 'POST');
    },
    {
      onSuccess: (response: any, data: any) => {
        queryClient.invalidateQueries(
          `survey-response-${data?.payload?.survey_id}`,
        );
        if (callback) callback(true, response, data);
      },
      onError: (error: any, data: any) => {
        if (callback) callback(false, error, data);
      },
    },
  );
};

export const useGetTranslation = ({
  surveyId,
  languageCode,
  isResponseTranslated,
  callback,
}: {
  surveyId: string | null | undefined;
  languageCode: string | null | undefined;
  isResponseTranslated: boolean | null | undefined;
  callback?: (isSuccess, response) => void;
}) =>
  useQuery(
    'response-translation',
    async () => {
      return await request(`/${surveyId}/translations/${languageCode}`);
    },
    {
      enabled: Boolean(isResponseTranslated),
      onSuccess: (response: any) => {
        if (callback) callback(true, response);
      },
      onError: (err) => {
        if (callback) callback(false, err);
      },
    },
  );

type UpdateTopicsType = {
  payload: {
    suggested_tags: {
      neutral: string[] | null;
      negative: string[] | null;
      positive: string[] | null;
    };
    customer_sentiment: string;
  };
  surveyId: string;
  responseId: string;
  surveyServResponseId: string;
};

export const useUpdateTopics = ({ setToast, callback }: MutationType) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation(
    async (params: UpdateTopicsType) => {
      const { payload, surveyId, responseId, surveyServResponseId } = params;
      if (!surveyId) return;
      const data = await request(
        `/surveys/${surveyId}/responses/${surveyServResponseId}/freddy-response-analysis`,
        payload,
        'PUT',
      );
      return data;
    },
    {
      onSuccess: (response, data) => {
        queryClient.invalidateQueries(`response-${data.responseId}-navigate`);
        if (setToast) {
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.topic_update_success')}`,
          });
        }
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (setToast) {
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.topic_update_error')}`,
          });
        }
      },
    },
  );
};
